(function () {
  const globalConfig = window.themeMorphDotsConfig || {};
  const DURATION_MS = typeof globalConfig.durationMs === "number" ? globalConfig.durationMs : 420;
  const N_POINTS = 48;

  function ease(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function dist(a, b) {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    return Math.hypot(dx, dy);
  }

  function resampleClosedPolyline(vertices, count) {
    const pts = vertices.slice();
    if (pts.length < 2) return pts;

    const segs = [];
    let total = 0;

    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      const len = dist(a, b);
      segs.push({ a, b, len });
      total += len;
    }

    const out = [];
    for (let i = 0; i < count; i++) {
      const target = (i / count) * total;
      let run = 0;
      for (let s = 0; s < segs.length; s++) {
        const seg = segs[s];
        if (run + seg.len >= target) {
          const local = seg.len === 0 ? 0 : (target - run) / seg.len;
          out.push([
            lerp(seg.a[0], seg.b[0], local),
            lerp(seg.a[1], seg.b[1], local)
          ]);
          break;
        }
        run += seg.len;
      }
    }
    return out;
  }

  function buildCirclePoints() {
    const cx = 5;
    const cy = 5;
    const r = 3.75;
    const start = Math.PI * 0.8;
    const pts = [];
    for (let i = 0; i < N_POINTS; i++) {
      const t = (i / N_POINTS) * Math.PI * 2 + start;
      pts.push([cx + Math.cos(t) * r, cy + Math.sin(t) * r]);
    }
    return pts;
  }

  function buildLambdaPoints() {
    const A = [1.9, 8.55];
    const B = [5.0, 1.35];
    const C = [8.1, 8.55];
    const D = [6.7, 8.55];
    const E = [5.0, 4.35];
    const F = [3.3, 8.55];
    return resampleClosedPolyline([A, B, C, D, E, F], N_POINTS);
  }

  function smoothClosedPath(points) {
    const n = points.length;
    if (!n) return "";

    const mid = (p1, p2) => [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];

    let d = "";
    const m0 = mid(points[n - 1], points[0]);
    d += `M${m0[0]},${m0[1]}`;
    for (let i = 0; i < n; i++) {
      const p = points[i];
      const m = mid(p, points[(i + 1) % n]);
      d += ` Q${p[0]},${p[1]} ${m[0]},${m[1]}`;
    }
    d += " Z";
    return d;
  }

  function interpolatePoints(fromPts, toPts, t) {
    const out = new Array(fromPts.length);
    for (let i = 0; i < fromPts.length; i++) {
      out[i] = [
        lerp(fromPts[i][0], toPts[i][0], t),
        lerp(fromPts[i][1], toPts[i][1], t)
      ];
    }
    return out;
  }

  const CIRCLE = buildCirclePoints();
  const LAMBDA = buildLambdaPoints();

  class MorphingDots {
    constructor(container) {
      this.container = container;
      this.sectionId = this.getSectionId(container);
      this.root = this.getRoot(container, this.sectionId);
      this.items = [];
      this.dots = [];
      this.rafMap = new WeakMap();
      this.prevBtn = null;
      this.nextBtn = null;
      this.initialized = false;
      this.init();
    }

    getSectionId(container) {
      if (container.dataset.sectionId) return container.dataset.sectionId;
      const cls = Array.from(container.classList).find((c) => c.indexOf("product-image-dots-") === 0);
      return cls ? cls.replace("product-image-dots-", "") : "";
    }

    getRoot(container, sectionId) {
      if (container.dataset.morphRoot) {
        const bySelector = document.querySelector(container.dataset.morphRoot);
        if (bySelector) return bySelector;
      }
      if (sectionId) {
        const scoped = document.querySelector(`[data-section-id="${sectionId}"]`);
        if (scoped) return scoped;
      }
      return container.closest("[data-section-id]") || container.closest(".product-single") || document;
    }

    isMobileOnly() {
      return this.container.dataset.morphMobileOnly === "true";
    }

    isMobileViewport() {
      const mobileMax = typeof globalConfig.mobileMax === "number" ? globalConfig.mobileMax : 991;
      return window.matchMedia(`(max-width: ${mobileMax}px)`).matches;
    }

    getItems() {
      if (this.container.dataset.morphSource) {
        return Array.from(this.root.querySelectorAll(this.container.dataset.morphSource));
      }

      if (this.sectionId) {
        const productThumbs = this.root.querySelectorAll(`.product-single__thumbnail-item-${this.sectionId}`);
        if (productThumbs.length) return Array.from(productThumbs);
      }

      const generic = this.root.querySelectorAll("[data-morph-item]");
      return Array.from(generic);
    }

    getActiveIndex() {
      const explicit = parseInt(this.container.dataset.morphActiveIndex, 10);
      if (!Number.isNaN(explicit) && explicit >= 0) {
        return Math.min(explicit, Math.max(0, this.items.length - 1));
      }

      const byState = this.items.findIndex(
        (el) => el.classList.contains("is-active") || el.hasAttribute("data-active")
      );
      return byState >= 0 ? byState : 0;
    }

    clearDots() {
      this.container.innerHTML = "";
      this.dots = [];
      this.unbindNav();
    }

    getNavButtons() {
      const wrap = this.container.closest("[data-morph-nav-wrap]") || this.container.parentElement;
      if (!wrap) return { prev: null, next: null };
      return {
        prev: wrap.querySelector("[data-morph-prev]"),
        next: wrap.querySelector("[data-morph-next]")
      };
    }

    unbindNav() {
      if (this.prevBtn && this.prevBtn.__morphNavClick) {
        this.prevBtn.removeEventListener("click", this.prevBtn.__morphNavClick);
        delete this.prevBtn.__morphNavClick;
      }
      if (this.nextBtn && this.nextBtn.__morphNavClick) {
        this.nextBtn.removeEventListener("click", this.nextBtn.__morphNavClick);
        delete this.nextBtn.__morphNavClick;
      }
      this.prevBtn = null;
      this.nextBtn = null;
    }

    init() {
      this.root = this.getRoot(this.container, this.sectionId);

      if (this.isMobileOnly() && !this.isMobileViewport()) {
        this.clearDots();
        this.initialized = false;
        return;
      }

      this.items = this.getItems();
      if (!this.items.length) {
        this.clearDots();
        this.initialized = false;
        return;
      }

      this.buildDots();
      this.bind();
      this.bindNav();
      this.setActive(this.getActiveIndex(), { animate: false, emit: false });
      this.initialized = true;
    }

    buildDots() {
      this.container.innerHTML = "";
      this.dots = [];

      this.items.forEach((_, i) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "morph-dot";
        btn.dataset.index = String(i);
        btn.setAttribute("aria-label", `Go to item ${i + 1}`);
        btn.__shape = "circle";
        btn.__points = CIRCLE.map((p) => [p[0], p[1]]);
        btn.innerHTML = `<svg viewBox="0 0 10 10" aria-hidden="true"><path d="${smoothClosedPath(btn.__points)}"></path></svg>`;

        const path = btn.querySelector("path");
        path.setAttribute("fill", "none");
        path.setAttribute("stroke", "var(--dot-color, #111)");
        path.setAttribute("stroke-width", "1.4");
        path.setAttribute("stroke-linecap", "round");
        path.setAttribute("stroke-linejoin", "round");

        this.container.appendChild(btn);
        this.dots.push(btn);
      });
    }

    bind() {
      this.dots.forEach((dot) => {
        dot.addEventListener("click", (e) => {
          e.preventDefault();
          const idx = parseInt(dot.dataset.index, 10);
          if (Number.isNaN(idx)) return;

          this.setActive(idx, { animate: true, emit: true });
          this.showItem(idx);
        });
      });
    }

    bindNav() {
      this.unbindNav();
      const nav = this.getNavButtons();
      this.prevBtn = nav.prev || null;
      this.nextBtn = nav.next || null;
      if (this.prevBtn) {
        const onPrev = (e) => {
          e.preventDefault();
          if (!this.items.length) return;
          const current = this.getActiveIndex();
          const next = (current - 1 + this.items.length) % this.items.length;
          this.setActive(next, { animate: true, emit: true });
          this.showItem(next);
        };
        this.prevBtn.addEventListener("click", onPrev);
        this.prevBtn.__morphNavClick = onPrev;
      }
      if (this.nextBtn) {
        const onNext = (e) => {
          e.preventDefault();
          if (!this.items.length) return;
          const current = this.getActiveIndex();
          const next = (current + 1) % this.items.length;
          this.setActive(next, { animate: true, emit: true });
          this.showItem(next);
        };
        this.nextBtn.addEventListener("click", onNext);
        this.nextBtn.__morphNavClick = onNext;
      }
    }

    showItem(i) {
      const item = this.items[i];
      if (!item) return;

      const clickTarget = item.querySelector("a, button");
      if (clickTarget && this.container.dataset.morphClickSource !== "false") {
        clickTarget.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
      }

      if (item.dataset.imageId) {
        this.root.querySelectorAll(".product-single__photo").forEach((el) => el.classList.add("hide"));
        const id = item.dataset.imageId;
        const node = this.root.querySelector(`#${CSS.escape(id)}`) || document.getElementById(id);
        if (node) node.classList.remove("hide");
      }

      this.container.dispatchEvent(
        new CustomEvent("morphdots:select", {
          bubbles: true,
          detail: { index: i, item, root: this.root }
        })
      );
    }

    animateDot(dot, targetShape, animate) {
      const path = dot.querySelector("path");
      if (!path) return;

      const toPts = targetShape === "lambda" ? LAMBDA : CIRCLE;
      const fromPts = dot.__points ? dot.__points : dot.__shape === "lambda" ? LAMBDA : CIRCLE;

      const prev = this.rafMap.get(path);
      if (prev) cancelAnimationFrame(prev);

      if (!animate) {
        dot.__shape = targetShape;
        dot.__points = toPts.map((p) => [p[0], p[1]]);
        path.setAttribute("d", smoothClosedPath(dot.__points));
        return;
      }

      const start = performance.now();
      const frame = (now) => {
        const t = Math.min((now - start) / DURATION_MS, 1);
        const e = ease(t);
        const pts = interpolatePoints(fromPts, toPts, e);
        dot.__points = pts;
        path.setAttribute("d", smoothClosedPath(pts));

        if (t < 1) {
          this.rafMap.set(path, requestAnimationFrame(frame));
        } else {
          dot.__shape = targetShape;
          dot.__points = toPts.map((p) => [p[0], p[1]]);
          path.setAttribute("d", smoothClosedPath(dot.__points));
          this.rafMap.delete(path);
        }
      };

      this.rafMap.set(path, requestAnimationFrame(frame));
    }

    setActive(activeIndex, opts) {
      if (!this.dots.length) return;
      const animate = opts && typeof opts.animate === "boolean" ? opts.animate : true;

      this.dots.forEach((dot, i) => {
        const active = i === activeIndex;
        dot.classList.toggle("is-active", active);
        if (active) dot.setAttribute("data-active", "");
        else dot.removeAttribute("data-active");
        this.animateDot(dot, active ? "lambda" : "circle", animate);
      });

      this.items.forEach((item, i) => {
        item.classList.toggle("is-active", i === activeIndex);
        if (i === activeIndex) item.setAttribute("data-active", "");
        else item.removeAttribute("data-active");
      });

      this.container.dataset.morphActiveIndex = String(activeIndex);
    }
  }

  function initAll() {
    document.querySelectorAll("[data-morph-dots]").forEach((container) => {
      if (container.__morph) container.__morph.init();
      else container.__morph = new MorphingDots(container);
    });
  }

  let rt;
  function onResize() {
    clearTimeout(rt);
    rt = setTimeout(initAll, 120);
  }

  document.addEventListener("DOMContentLoaded", initAll);
  window.addEventListener("load", initAll);
  window.addEventListener("resize", onResize);
  document.addEventListener("shopify:section:load", initAll);

  if (window.barba && window.barba.hooks) {
    window.barba.hooks.afterEnter(() => initAll());
  }
})();
